import React, { useState } from "react";
// UI Frameworks
import { toast } from "react-toastify";
// Utilities
import UserService from "./services/endpoints/user";
import OrderService from "./services/endpoints/order";
import CollateralService from "./services/endpoints/collateral";
// Types
import { SelectOptionType } from "src/VirtualOMS/types/common";
import { OrderTableRow } from "src/VirtualOMS/types/order";
import { IPortfoCloneDTO } from "./types/portfo";
import { clonePortfo } from "./services/endpoints/portfo";
import {
  CollateralStrategyRow,
  VirtualOmsEventLogRow,
} from "./types/collateral";

export function useVirtualOMS() {
  // local store
  const [balance, setBalance] = React.useState(0);
  const [mainBuyPower, setMainBuyPower] = React.useState(0);
  const [optionBuyPower, setOptionBuyPower] = React.useState(0);
  const [credit, setCredit] = React.useState(0);
  const [optionCredit, setOptionCredit] = React.useState(0);
  const [vajhTazmin, setVajhTazmin] = React.useState(0);
  const [accounts, setAccounts] = React.useState<any[]>([]);
  const [user, setUser] = React.useState<SelectOptionType>();
  const [openOrders, setOpenOrders] = React.useState<OrderTableRow[]>([]);
  const [tradeOrders, setTradeOrders] = React.useState<OrderTableRow[]>([]);
  const [collateralStrategies, setCollateralStrategies] = React.useState<
    CollateralStrategyRow[]
  >([]);
  const [eventLogs, setEventLogs] = React.useState<VirtualOmsEventLogRow[]>([]);
  const [activeTab, setActiveTab] = React.useState<
    "OPENOrder" | "TradeOrder" | "Collateral" | "EventLog"
  >("OPENOrder");
  const [resetFlag, setResetFlag] = useState(false);
  const [cloneSource, setCloneSource] = useState({} as SelectOptionType);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    setLoading(true);
    UserService.getDropdown()
      .then((resp) => {
        setAccounts(resp);
        setUser(resp[0]);
      })
      .catch((err) => console.log("===err", err))
      .finally(() => setLoading(false));
  }, []);

  const refreshData = React.useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      if (activeTab === "OPENOrder") {
        setOpenOrders(await OrderService.getOrderById(user.id as number));
      } else if (activeTab === "TradeOrder") {
        setTradeOrders(
          await OrderService.getTrades({
            userId: user.id as number,
            userName: user.name,
          })
        );
      } else if (activeTab === "Collateral") {
        setCollateralStrategies(await CollateralService.getStrategies(user.id as number));
      } else {
        setEventLogs(await CollateralService.getEventLogs(user.id as number));
      }
    } catch {
      // A periodic refresh must not leave the screen in a loading state when its API is unavailable.
    } finally {
      setLoading(false);
      setResetFlag(false);
    }
  }, [activeTab, user?.id, user?.name]);

  React.useEffect(() => {
    if (!user?.id) return;

    UserService.getUser({ userId: user.id as number, userName: "" })
      .then((account) => {
        setBalance(Number(account.remain || 0));
        setMainBuyPower(Number(account.buyingPower || 0));
        setOptionBuyPower(Number(account.optionPower || 0));
        setCredit(Number(account.credit || 0));
        setOptionCredit(Number(account.optionCredit || 0));
        setVajhTazmin(Number(account.marginBlock || 0));
      })
      .catch(() => toast.error("something is wrong with get account powers!"));
  }, [user?.id]);

  React.useEffect(() => {
    if (!user?.id) return;

    refreshData();
    const intervalId = window.setInterval(refreshData, 10_000);
    return () => window.clearInterval(intervalId);
  }, [refreshData, user?.id]);

  React.useEffect(() => {
    if (!resetFlag) return;
    refreshData();
  }, [refreshData, resetFlag]);

  const handleAddRemainingBalance = () => {
    setLoading(true);
    UserService.setBalanceByUserId({ balance, id: (user?.id || 0) as number })
      .then(() => {
        toast.success("set Remaining Balance successfully");
        setResetFlag(true);
      })
      .catch(() => toast.error("something is wrong!"))
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetAccount = () => {
    setLoading(true);
    OrderService.resetUserAccount((user?.id || -1) as number)
      .then(() => {
        toast.success("Account reset successfully");
        setResetFlag(true);
      })
      .catch(() => toast.error("something is wrong!"))
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSetBuyingPowers = () => {
    setLoading(true);
    UserService.setBuyingPowersByUserId({
      id: (user?.id || 0) as number,
      mainBuyPower,
      optionBuyPower,
      credit,
      optionCredit,
      vajhTazmin,
    })
      .then(() => toast.success("buying powers set successfully"))
      .catch(() => toast.error("something is wrong!"))
      .finally(() => setLoading(false));
  };

  const handleBalanceChange = (value: string) => {
    setBalance(Number(value));
  };

  const clonePorfoAccount = () => {
    setLoading(true);
    clonePortfo({
      sourceUserId: parseInt(cloneSource.id?.toString()),
      targetUsername: user?.name || "",
    })
      .then((resp) => {
        if (!resp.data) toast.error("Something went Wrong!");
        else {
          toast.success("Account Cloned Successfully!");
          setResetFlag(true);
        }
      })
      .catch(() => toast.error("Something Went Wrong!"))
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClearEventLogs = () => {
    setLoading(true);
    CollateralService.clearEventLogs((user?.id || -1) as number)
      .then(() => {
        setEventLogs([]);
        toast.success("Event log cleared successfully");
      })
      .catch(() => toast.error("something is wrong with clearing event log!"))
      .finally(() => setLoading(false));
  };

  const handleClearCollateralStrategies = () => {
    setLoading(true);
    CollateralService.clearStrategies((user?.id || -1) as number)
      .then(() => {
        setCollateralStrategies([]);
        toast.success("Collateral strategies cleared successfully");
      })
      .catch(() => toast.error("something is wrong with clearing collateral strategies!"))
      .finally(() => setLoading(false));
  };

  const handleCancelOpenOrders = async () => {
    if (!user?.id || openOrders.length === 0) return;

    setLoading(true);
    try {
      const response = await OrderService.cancelOpenOrdersById(
        user.id as number,
        openOrders.map((order) => order.id)
      );
      if (response?.data === false) throw new Error("Cancel open orders failed");

      toast.success("Open orders canceled successfully");
      await refreshData();
    } catch {
      toast.error("something is wrong with canceling open orders!");
    } finally {
      setLoading(false);
    }
  };

  const orders = activeTab === "TradeOrder" ? tradeOrders : openOrders;

  return {
    balance,
    mainBuyPower,
    optionBuyPower,
    credit,
    optionCredit,
    vajhTazmin,
    accounts,
    user,
    orders,
    collateralStrategies,
    eventLogs,
    activeTab,
    setActiveTab,
    setUser,
    handleAddRemainingBalance,
    handleBalanceChange,
    handleSetBuyingPowers,
    setMainBuyPower,
    setOptionBuyPower,
    setCredit,
    setOptionCredit,
    setVajhTazmin,
    handleResetAccount,
    setResetFlag,
    resetFlag,
    setCloneSource,
    cloneSource,
    clonePorfoAccount,
    handleClearEventLogs,
    handleClearCollateralStrategies,
    handleCancelOpenOrders,
    loading,
  };
}
